import Vue from 'vue';
import AuthSvc from '@/services/AuthSvc';
import UserSvc from '@/services/UserSvc';
import { clearAuthSession } from '@/services/_AxiosInstance';

const RESET_ACTIVE_COMPANY_STATE = 'reset_active_company_state';
const RESET_USER_DATA = 'reset_user_data';
const SET_ACTIVE_COMPANY = 'set_active_company';
const SET_ACTIVE_APP = 'set_active_app';
const SET_AVAILABLE_COMPANY = 'set_available_company';
const SET_USER_ACCESS = 'set_user_access';
const SET_USER_ID = 'set_user_id';
const SET_USER_TOKEN = 'set_user_token';
const SET_IS_SUPER_ADMIN = 'set_is_super_admin';
const SET_IS_GPS_VIEWER = 'set_is_gps_viewer';

// noinspection JSUnusedGlobalSymbols
const auth = {
  namespaced: true,
  state: {
    activeCompany: undefined,
    activeGroup: undefined,
    activeApp: undefined,
    availableCompany: [],
    userAccess: {},
    userId: undefined,
    isAuthenticated: false,
    isSuperAdmin: false,
    isGpsViewer: false,
  },
  getters: {
    activeCompany(state) {
      if (state.activeCompany) {
        state.activeCompany = JSON.parse(localStorage.getItem('activeCompany'));
      }

      return state.activeCompany;
    },
    activeGroup(state) {
      if (state.activeGroup) {
        state.activeGroup = JSON.parse(localStorage.getItem('activeGroup'));
      }

      return state.activeGroup;
    },
    activeApp(state) {
      if (!state.activeApp) {
        state.activeApp = JSON.parse(localStorage.getItem('activeApp'));
      }

      return state.activeApp;
    },
    isAuthenticated(state) {
      state.isAuthenticated = parseInt(localStorage.getItem('isAuthenticated')) === 1;
      return state.isAuthenticated;
    },
    userAccess(state) {
      return state.userAccess;
    },
    isSuperAdmin(state) {
      return state.isSuperAdmin;
    },
    isGpsViewer(state) {
      return state.isGpsViewer;
    },
    availableCompany(state) {
      return state.availableCompany;
    },
    availableGroup(state) {
      const availableCompanies = state.availableCompany;
      return Vue._.uniqBy(Object.values(availableCompanies).map((v) => v.compGrcp), (v) => v.grcpId);
    },
    isAuthorized(state) {
      return (module, action) => {
        const access = state.userAccess[module];
        if (Array.isArray(access)) {
          return access.find(action) !== -1;
        }
        return false;
      };
    },
  },
  mutations: {
    [RESET_ACTIVE_COMPANY_STATE](state) {
      state.activeCompany = undefined;
      state.activeGroup = undefined;
      localStorage.removeItem('activeCompany');
      localStorage.removeItem('activeGroup');
    },
    [RESET_USER_DATA](state) {
      state.userId = undefined;
      state.userAccess = [];
      state.availableCompany = [];
      state.isSuperAdmin = false;
      state.isGpsViewer = false;
    },
    [SET_ACTIVE_COMPANY](state, { company, group }) {
      state.activeCompany = company;
      state.activeGroup = group;
      localStorage.setItem('activeCompany', JSON.stringify(company));
      localStorage.setItem('activeGroup', JSON.stringify(group));
    },
    [SET_ACTIVE_APP](state, v) {
      state.activeApp = v;
      localStorage.setItem('activeApp', JSON.stringify(v));
    },
    [SET_USER_ACCESS](state, v) {
      state.userAccess = v;
    },
    [SET_USER_TOKEN](state, v) {
      state.isAuthenticated = v;
      localStorage.setItem('isAuthenticated', v);
    },
    [SET_USER_ID](state, v) {
      localStorage.setItem('userId', v);
      state.userId = v;
    },
    [SET_IS_SUPER_ADMIN](state, v) {
      state.isSuperAdmin = v;
    },
    [SET_IS_GPS_VIEWER](state, v) {
      state.isGpsViewer = v;
    },
    [SET_AVAILABLE_COMPANY](state, v) {
      state.availableCompany = v;
    },
  },
  actions: {
    async signIn({ commit }, token) {
      try {
        await AuthSvc.signInWithToken(token);
        commit(SET_USER_TOKEN, 1);
      } catch (e) {
        commit(SET_USER_TOKEN, 0);
        throw e;
      }
    },
    logout({ commit }) {
      commit(SET_USER_TOKEN, 0);
      commit(SET_IS_SUPER_ADMIN, false);
      commit(SET_IS_GPS_VIEWER, false);
      clearAuthSession();
      AuthSvc.logout();

      commit(RESET_ACTIVE_COMPANY_STATE);
    },
    async changeActiveCompany({ commit }, { company, group }) {
      commit(RESET_ACTIVE_COMPANY_STATE);
      commit(SET_ACTIVE_COMPANY, { company, group });

      await AuthSvc.setCompany(company.compId);
    },
    async getUser({ commit }) {
      commit(RESET_USER_DATA);

      const userStatus = await AuthSvc.getUserStatus();
      const availableCompany = userStatus.data.company ? userStatus.data.company.available : {};

      commit(SET_USER_TOKEN, 1);
      commit(SET_USER_ID, userStatus.data.user);
      commit(SET_USER_ACCESS, userStatus.data.access);
      commit(SET_AVAILABLE_COMPANY, availableCompany);

      try {
        const res = await UserSvc.getUserRoleByUserId(userStatus.data.user);
        const roles = Array.isArray(res.data.data) ? res.data.data : [];
        const isSuperAdmin = roles.some((role) => role.usroRole && role.usroRole.roleName === 'Superadmin');
        const isGpsViewer = roles.some((role) => role.usroRole && role.usroRole.roleName === 'GPS Viewer');
        commit(SET_IS_SUPER_ADMIN, isSuperAdmin);
        commit(SET_IS_GPS_VIEWER, isGpsViewer);
      } catch (e) {
        commit(SET_IS_SUPER_ADMIN, false);
        commit(SET_IS_GPS_VIEWER, false);
      }

      if (userStatus.data.company && userStatus.data.company.active) {
        let companyActive = userStatus.data.company.active;

        if (Vue._.isNumber(companyActive)) {
          companyActive = availableCompany[companyActive];
        }

        commit(SET_ACTIVE_COMPANY, {
          company: companyActive,
          group: companyActive.compGrcp,
        });
      } else {
        commit(SET_USER_TOKEN, 0);

        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('userId');

        commit(RESET_ACTIVE_COMPANY_STATE);
        window.location.href = '/auth/login';
        // console.log(userStatus);
      }
    },
    async changeActiveApp({ commit }, app) {
      commit(SET_ACTIVE_APP, app);
    },
  },
};

export default auth;
